import React from 'react';
// import Cookies from 'js-cookie';

import PoweredBy from './PoweredBy';
import ViewContainer from './ViewContainer';
import Spinner from './Spinner';
import * as loginService from '../services/login';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

export default function PasswordEntry(props) {
  const isMobilePasswordManagerEnabled = props.mobilePasswordManagerEnabled !== false;

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const [state, setState] = useState({
    isLoading: false,
    username: queryParams.get('login_hint'),
    password: '',
    infoMessage: props.infoMessage,
    errorMessage: props.errorMessage,
    isSubmittingPassword: false,
  });

  function onChange (field, value) {
    if (!value.length) {
      setState({
        ...state,
        password: '',
        errorMessage: 'You must enter your password.',
      });
    } else {
      setState({ ...state, [field]: value, errorMessage: '' });
    }
  }

  function prepareForgotPassword () {
    if (!props.resetPasswordEnabled) {
      return null;
    }

    return (
      <a
        className='forgot-password'
        onClick={(event) => event.preventDefault() || navigate(`/forgotPassword?login_hint=${state.username}`)}
        href="#"
      >Forgot password?</a>
    );
  }

  // Request saved credentials as soon as the password entry page mounts.
  // We use useEffect rather than relying on onFocus because autoFocus in WKWebView
  // does not consistently fire React's onFocus (programmatic focus bypasses React's
  // synthetic event delegation). useEffect is guaranteed to run after mount.
  useEffect(() => {
    if (!isMobilePasswordManagerEnabled) return;
    if (window.webkit?.messageHandlers?.requestCredential) { // iOS credential bridge present
      window.webkit.messageHandlers.requestCredential.postMessage({username: state.username || '',});
    } else if (window.Android?.requestCredential) { // Android credential bridge present
      window.Android.requestCredential(state.username || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login () {
    const {
      username,
      password,
    } = state;

    if (!password) {
      setState({
        ...state,
        infoMessage: '',
        errorMessage: 'You must enter your password.',
      });

      return;
    }

    setState({
      ...state,
      infoMessage: 'Logging you in...',
      errorMessage: '',
      isSubmittingPassword: true,
    });

    try {
      const response = await loginService.login(username, password);
      if(response.status === 200) {
        const body = await response.json();

        // Trigger native iOS "Save/Update Password?" prompt via the private Keychain bridge.
        // NativeWebViewPlugin handles this message by querying the device Keychain and
        // presenting a UIAlertController ("Save Password?" or "Update Password?") as
        // appropriate. Credentials are stored in the private device Keychain only — no
        // iCloud sync, no entry in the system Passwords app.
        //
        // window.webkit.messageHandlers is available because NativeWebViewPlugin registers
        // a "storeCredential" WKScriptMessageHandler on the portal WKWebView.
        // In plain Safari (or any non-Capacitor context) this object is undefined and the
        // block is skipped gracefully.
        if (isMobilePasswordManagerEnabled) {
          if (window.webkit?.messageHandlers?.storeCredential) { // iOS credential bridge present
            window.webkit.messageHandlers.storeCredential.postMessage({
              username,
              password,
            });
          } else if (window.Android?.storeCredential) { // Android credential bridge present
            window.Android.storeCredential(username, password);
          }
        }

        window.location.assign(body.redirectPath);
        return;
      }
      else if(response.status === 401) {
        setState({
          ...state,
          infoMessage: '',
          errorMessage: 'Your username or password is incorrect.',
          isSubmittingPassword: false,
        });
        return;
      }
      
      setState({
        ...state,
        infoMessage: '',
        errorMessage: 'Something went wrong while attempting to login. If this persists contact your system administrator.',
        isSubmittingPassword: false,
      });
      return;
    } catch (error) {
      console.error(error);
      setState({
        ...state,
        infoMessage: '',
        errorMessage: 'Something went wrong while attempting to login. If this persists contact your system administrator.',
        isSubmittingPassword: false,
      });
    }
  }


  function prepareLoginForm () {
    const {
      username,
      password,
      isSubmittingPassword,
    } = state;

    if (isSubmittingPassword) {
      return (
        <Spinner />
      );
    }

    return (
      <div className='auth-form-wrapper'>
        <form className='auth-form' onSubmit={(e) => { e.preventDefault(); login(); }}>
          <div className='input-container form-group'>
            <div className='back-link-container'>
              <a
                className="back-link"
                onClick={(e) => e.preventDefault() || navigate('/')}
                aria-label="Go Back"
                href="#"
              >
                <i className="fa-solid fa-circle-chevron-left" aria-hidden="true"></i>
                {/* @todo: get the pro license of Font Awesome used correctly for dev and production */}
                {/* <i className="fa-light fa-circle-chevron-left" aria-hidden="true"></i> */}
              </a>
              <label
                htmlFor='username'
              >
                Username
              </label>
            </div>
            <input
              id='username'
              name='username'
              type='text'
              className='form-control'
              value={username}
              readOnly={true}
              autoComplete={isMobilePasswordManagerEnabled ? 'username' : 'off'}
              tabIndex={-1}
            />
          </div>
          <div className='input-container form-group'>
            <label
              htmlFor='password'
            >
              Password
            </label>
            <input
              className='form-control'
              id='password'
              name='password'
              type='password'
              autoComplete={isMobilePasswordManagerEnabled ? 'current-password' : 'off'}
              placeholder='Enter Password'
              aria-label='Password'
              required
              autoFocus={true}
              onChange={(e) => onChange('password', e.target.value)}
              value={password}
              aria-describedby="error-message"
            />
            {/* Note: password autofill functionality in the mobile app is reliant on the autocomplete property on the above input */}
          </div>
          <div className='submit-container'>
            <button
              className='btn btn-primary'
              type='submit'
              disabled={!state.password}
            >Next</button>
            {prepareForgotPassword()}
          </div>
        </form>
        <PoweredBy/>
      </div>
    );
  }

  return (
    <ViewContainer
      className='login'
      version={props.version}
      title={props.title}
      now={props.now}
      errorMessage={state.errorMessage}
      infoMessage={state.infoMessage}
    >
      {prepareLoginForm()}
    </ViewContainer>
  );
}
